import React, { useEffect, useMemo, useState } from 'react';
import { Segmented, Input, Button, Tag, Spin, Empty, Tooltip } from 'antd';
import { CloseOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { makePostCall } from '../../utils/helper';

/**
 * HOD assign/unassign side-panel (old-PACS parity).
 *
 * Docks to the LEFT of the study grid so the HOD can pick a radiologist here and
 * studies on the right at the same time, then Assign — no modal round-trip.
 *
 * Roster comes from POST /assign-roster and lists radiologists AND hods (a HOD
 * can hand work to another HOD). Per row:
 *   - online:  live WebSocket presence (green dot)
 *   - Rep.:    studies signed off within the Today/Yesterday window
 *   - Pend.:   live backlog currently assigned to them (not date-limited)
 *
 * Assignment itself is delegated to the parent (onAssign / onUnassign) which
 * already owns the selected studies + the /assign-to-user call + grid refresh.
 */

export interface RosterUser {
  username: string;
  user_fullname: string;
  user_type: string;
  modalities: string[];
  online: boolean;
  rep_count: number;
  pend_count: number;
}

interface AssignPanelProps {
  visible: boolean;
  onClose: () => void;
  /** Study records currently checked in the grid. */
  selectedStudyRecords: any[];
  /** Center codes the list is filtered to (passed straight to /assign-roster). */
  centers?: string[];
  /** Assign the selected studies to this radiologist/hod username. */
  onAssign: (username: string) => void;
  /** Clear the assignment on the selected studies. */
  onUnassign: () => void;
  /** Bump to force a roster re-fetch (parent bumps it after an assign lands). */
  refreshKey?: number;
}

const AssignPanel: React.FC<AssignPanelProps> = ({
  visible,
  onClose,
  selectedStudyRecords,
  centers,
  onAssign,
  onUnassign,
  refreshKey = 0,
}) => {
  const [day, setDay] = useState<'today' | 'yesterday'>('today');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [roster, setRoster] = useState<RosterUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRad, setSelectedRad] = useState<string | null>(null);

  const centersKey = (centers || []).join(',');

  const fetchRoster = () => {
    setLoading(true);
    makePostCall('/assign-roster', { day, centers: centers || [] })
      .then(res => {
        setRoster(res.data?.data || []);
      })
      .catch(() => {
        setRoster([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch when the panel opens, the day toggle changes, the site filter changes,
  // or the parent signals a post-assign refresh.
  useEffect(() => {
    if (!visible) return;
    fetchRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, day, centersKey, refreshKey]);

  // Signed-off studies are locked (non-reassignable). If any are selected we
  // block the whole action and ask the HOD to deselect them — keeps bulk intent
  // explicit rather than silently skipping.
  const lockedCount = useMemo(
    () =>
      (selectedStudyRecords || []).filter(
        (r: any) => r?.order_workflow?.ow_pacs_status === 'SIGNEDOFF'
      ).length,
    [selectedStudyRecords]
  );
  const totalSelected = selectedStudyRecords?.length || 0;
  const hasLocked = lockedCount > 0;
  const eligibleCount = totalSelected - lockedCount;

  const visibleRoster = useMemo(() => {
    const term = search.trim().toLowerCase();
    return roster.filter(u => {
      if (onlineOnly && !u.online) return false;
      if (!term) return true;
      return (
        (u.user_fullname || '').toLowerCase().includes(term) ||
        (u.username || '').toLowerCase().includes(term) ||
        (u.modalities || []).some(m => m.toLowerCase().includes(term))
      );
    });
  }, [roster, onlineOnly, search]);

  const onlineTotal = useMemo(() => roster.filter(u => u.online).length, [roster]);

  if (!visible) return null;

  const canAssign = !!selectedRad && !hasLocked && eligibleCount > 0;
  const canUnassign = !hasLocked && totalSelected > 0;

  return (
    <div className="assign-panel">
      <div className="assign-panel__header">
        <span className="assign-panel__title">Assign Studies</span>
        <Tooltip title="Close">
          <button className="assign-panel__close" onClick={onClose} aria-label="Close">
            <CloseOutlined />
          </button>
        </Tooltip>
      </div>

      {/* Filters: online presence + day window driving the counts */}
      <div className="assign-panel__filters">
        <div className="assign-panel__filter-row">
          <span className="assign-panel__filter-label">User</span>
          <Segmented
            size="small"
            value={onlineOnly ? 'online' : 'all'}
            onChange={val => setOnlineOnly(val === 'online')}
            options={[
              { label: `Online${onlineTotal ? ` (${onlineTotal})` : ''}`, value: 'online' },
              { label: 'All', value: 'all' },
            ]}
          />
        </div>
        <div className="assign-panel__filter-row">
          <span className="assign-panel__filter-label">Day</span>
          <Segmented
            size="small"
            value={day}
            onChange={val => setDay(val as 'today' | 'yesterday')}
            options={[
              { label: 'Today', value: 'today' },
              { label: 'Yesterday', value: 'yesterday' },
            ]}
          />
        </div>
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Find radiologist"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Selection summary */}
      <div className="assign-panel__summary">
        <Tag color="blue" style={{ margin: 0 }}>{`${totalSelected} selected`}</Tag>
        {hasLocked && (
          <Tooltip title="Signed-off studies can't be reassigned. Deselect them to continue.">
            <Tag color="red" style={{ margin: 0 }}>{`${lockedCount} signed-off`}</Tag>
          </Tooltip>
        )}
      </div>

      {/* Column headers */}
      <div className="assign-panel__list-head">
        <span className="assign-panel__col-name">Radiologist</span>
        <span className="assign-panel__col-count">Rep.</span>
        <span className="assign-panel__col-count">Pend.</span>
      </div>

      {/* Roster list */}
      <div className="assign-panel__list">
        {loading ? (
          <div className="assign-panel__center">
            <Spin size="small" />
          </div>
        ) : visibleRoster.length === 0 ? (
          <div className="assign-panel__center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No radiologists" />
          </div>
        ) : (
          visibleRoster.map(u => {
            const isSel = selectedRad === u.username;
            return (
              <button
                key={u.username}
                type="button"
                className={`assign-panel__row${isSel ? ' assign-panel__row--selected' : ''}`}
                onClick={() => setSelectedRad(isSel ? null : u.username)}
              >
                <span className="assign-panel__col-name">
                  <span
                    className={`assign-panel__dot assign-panel__dot--${u.online ? 'on' : 'off'}`}
                    title={u.online ? 'Online' : 'Offline'}
                  />
                  <span className="assign-panel__name-block">
                    <span className="assign-panel__name">
                      {u.user_fullname}
                      {u.user_type === 'hod' && (
                        <Tag className="assign-panel__hod-tag" color="purple">HOD</Tag>
                      )}
                    </span>
                    {u.modalities.length > 0 && (
                      <span className="assign-panel__mods">{u.modalities.join(', ')}</span>
                    )}
                  </span>
                </span>
                <span className="assign-panel__col-count assign-panel__rep">{u.rep_count}</span>
                <span className="assign-panel__col-count assign-panel__pend">{u.pend_count}</span>
              </button>
            );
          })
        )}
      </div>

      {/* Actions */}
      <div className="assign-panel__actions">
        <Tooltip title="Reload counts & presence">
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchRoster} loading={loading} />
        </Tooltip>
        <Button
          size="small"
          danger
          disabled={!canUnassign}
          onClick={onUnassign}
        >
          Unassign
        </Button>
        <Button
          size="small"
          type="primary"
          disabled={!canAssign}
          onClick={() => selectedRad && onAssign(selectedRad)}
        >
          {`Assign${eligibleCount ? ` (${eligibleCount})` : ''}`}
        </Button>
      </div>
    </div>
  );
};

export default AssignPanel;
